import { Controller } from "@hotwired/stimulus";
import { GroupProfileDomHelpers } from "../services/GroupProfileDomHelpers";

/**
 * Main orchestrator controller for Product Group Profile Step
 *
 * Responsibilities:
 * - Initializes and coordinates other GroupProfile controllers
 * - Manages accordion expand/collapse behavior
 * - Handles URL parameter for jump-back navigation (edit_step)
 * - Coordinates initial page load sequence
 * - Manages submit button disabled state based on validation
 *
 * Works with:
 * - product_group_profile_validation_controller
 * - product_group_profile_accommodation_controller
 * - product_group_profile_quantity_controller
 * - product_group_profile_price_controller
 */
export default class ProductGroupProfileStepController extends Controller {
  static values = {
    existingSelections: {
      type: Object,
      default: {},
    },
    bookingId: Number,
    hasAccommodation: Boolean,
  };

  static targets = ["details", "loadingOverlay", "submitButton"];

  connect() {
    this.isInitializing = true;
    this.initializationComplete = false;

    this.setupAccordionBehavior();
    this.initializeFormSubmission();
    this.setupEventListeners();

    this.isInitializing = false;

    setTimeout(() => {
      this.initializeExistingSelections();

      setTimeout(() => {
        this.runCompleteInitialization();
      }, 50);
    }, 50);

    this.handleExpandGroupParameter();
    this.enforceAccommodationFirst();
  }

  /**
   * Run complete initialization sequence
   * Updates summaries, checks validation, updates submit button
   */
  runCompleteInitialization() {
    if (this.initializationComplete) {
      return;
    }

    GroupProfileDomHelpers.dispatchEvent(
      this.element,
      "group-profile:update-all-summaries"
    );

    this.updateSubmitButtonState();
    this.initializationComplete = true;
  }

  setupAccordionBehavior() {
    this.detailsTargets.forEach((details) => {
      const summary = details.querySelector("summary");
      if (!summary) return;

      summary.addEventListener("click", (event) => {
        event.preventDefault();

        const wasOpen = details.hasAttribute("open");

        this.detailsTargets.forEach((other) => {
          other.removeAttribute("open");
        });

        if (!wasOpen) {
          details.setAttribute("open", "open");
        }
      });
    });
  }

  initializeExistingSelections() {
    const existingSelections = this.existingSelectionsValue || {};

    GroupProfileDomHelpers.dispatchEvent(
      this.element,
      "group-profile:load-existing-selections",
      {
        selections: existingSelections,
      }
    );
  }

  initializeFormSubmission() {
    const form = this.element.closest("form");
    if (!form) return;

    form.addEventListener("submit", (event) => {
      const validationEvent = new CustomEvent("group-profile:validate-form", {
        bubbles: true,
        cancelable: true,
        detail: { submissionAttempted: true },
      });

      this.element.dispatchEvent(validationEvent);

      if (validationEvent.defaultPrevented) {
        event.preventDefault();
      } else {
        GroupProfileDomHelpers.dispatchEvent(
          this.element,
          "group-profile:sync-to-form"
        );
      }
    });
  }

  handleExpandGroupParameter() {
    const urlParams = new URLSearchParams(window.location.search);
    const expandGroup = urlParams.get("expand_group");

    if (!expandGroup) return;

    this.detailsTargets.forEach((details) => {
      details.removeAttribute("open");
    });

    let targetElement = this.detailsTargets.find((details) => {
      return details.dataset.resourceType === expandGroup;
    });

    if (!targetElement) {
      const accommodationSection = this.element.querySelector(
        `[data-resource-type="\${expandGroup}"]`
      );
      if (accommodationSection) {
        targetElement = accommodationSection.querySelector("details");
      }
    }

    if (targetElement) {
      if (targetElement.tagName.toLowerCase() === "details") {
        targetElement.setAttribute("open", "open");
      }

      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  setupEventListeners() {
    this.element.addEventListener("group-profile:set-loading", (event) => {
      this.showLoadingOverlay(event.detail.loading);
    });

    this.element.addEventListener(
      "group-profile:validation-state-changed",
      () => {
        this.updateSubmitButtonState();
      }
    );

    this.element.addEventListener("group-profile:quantity-changed", () => {
      this.updateSubmitButtonState();
    });

    this.element.addEventListener("group-profile:accommodation-changed", () => {
      this.updateSubmitButtonState();
      // Re-check if we should enable other resources
      setTimeout(() => this.enforceAccommodationFirst(), 100);
    });

    this.element.addEventListener("group-profile:room-selected", () => {
      // Re-check after room selection
      setTimeout(() => this.enforceAccommodationFirst(), 100);
    });

    this.element.addEventListener("group-profile:room-removed", () => {
      // Re-check after room removal
      setTimeout(() => this.enforceAccommodationFirst(), 100);
    });
  }

  updateSubmitButtonState() {
    if (!this.hasSubmitButtonTarget) {
      return;
    }

    const validationEvent = new CustomEvent("group-profile:validate-form", {
      bubbles: true,
      cancelable: true,
      detail: { submissionAttempted: false },
    });

    this.element.dispatchEvent(validationEvent);

    const isValid = !validationEvent.defaultPrevented;

    if (isValid) {
      this.submitButtonTarget.disabled = false;
      this.submitButtonTarget.classList.remove(
        "opacity-30",
        "pointer-events-none"
      );
    } else {
      this.submitButtonTarget.disabled = true;
      this.submitButtonTarget.classList.add(
        "opacity-30",
        "pointer-events-none"
      );
    }
  }

  showLoadingOverlay(value) {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.style.display = value ? "flex" : "none";
    }
  }

  isCurrentlyInitializing() {
    return this.isInitializing;
  }

  /**
   * Enforce that accommodation must be selected before other resources
   * Disables and grays out other resource sections if no accommodation is selected
   */
  enforceAccommodationFirst() {
    // Check if any accommodation/room has been selected
    const hasAccommodationSelected = this.checkAccommodationSelected();

    if (hasAccommodationSelected) {
      // Accommodation selected - enable all other resources
      this.enableOtherResources();
    } else {
      // No accommodation selected - disable other resources
      this.disableOtherResources();
    }
  }

  /**
   * Check if any accommodation has been selected by looking for booking rooms
   */
  checkAccommodationSelected() {
    // Use the server-side value which checks booking_room_assignments
    return this.hasAccommodationValue === true;
  }

  /**
   * Disable all non-accommodation resource sections
   */
  disableOtherResources() {
    this.detailsTargets.forEach((details) => {
      const { resourceType } = details.dataset;

      // Skip accommodation resources
      if (resourceType === "accommodation") return;

      // Add disabled styling
      details.classList.add("opacity-50", "pointer-events-none");

      // Add a notice if it doesn't exist
      if (!details.querySelector(".accommodation-required-notice")) {
        const summary = details.querySelector("summary");
        if (summary) {
          const notice = document.createElement("div");
          notice.className =
            "accommodation-required-notice text-xs text-gray-500 mt-1 font-normal";
          notice.textContent = "Bitte wählen Sie zuerst eine Unterkunft";

          const titleDiv = summary.querySelector(".text-lg");
          if (titleDiv && titleDiv.parentElement) {
            titleDiv.parentElement.appendChild(notice);
          }
        }
      }
    });
  }

  /**
   * Enable all resource sections
   */
  enableOtherResources() {
    this.detailsTargets.forEach((details) => {
      // Remove disabled styling
      details.classList.remove("opacity-50", "pointer-events-none");

      // Remove notice
      const notice = details.querySelector(".accommodation-required-notice");
      if (notice) {
        notice.remove();
      }
    });
  }
}
